// Scoring, theme, and issue classification helpers extracted from App.js.
// Pure functions and constants only — no React.

export const SCORE_HERO_IMAGES = {
  high: 'https://customer-assets.emergentagent.com/job_8aa3cc5a-c8a5-4d60-856d-1a56be98d680/artifacts/5snc4i49_file_00000000efe871f48de38b2213c2b8b9.png',
  medium: 'https://customer-assets.emergentagent.com/job_8aa3cc5a-c8a5-4d60-856d-1a56be98d680/artifacts/2bbzhtyf_file_000000007e3471f48c61def0d0801346.png',
  low: 'https://customer-assets.emergentagent.com/job_8aa3cc5a-c8a5-4d60-856d-1a56be98d680/artifacts/wm7bus6n_file_00000000fb8c7246ba51c6f69366ac71.png',
};

export const WORTHIFY_EXPERT_IMAGE = 'https://customer-assets.emergentagent.com/job_8aa3cc5a-c8a5-4d60-856d-1a56be98d680/artifacts/3i81jbr4_file_00000000fd6c7243b7fe6da471feedeb.png';

export const getScoreHeroImage = (riskLevel = 'LOW') => {
  if (riskLevel === 'HIGH') return SCORE_HERO_IMAGES.high;
  if (riskLevel === 'MEDIUM') return SCORE_HERO_IMAGES.medium;
  return SCORE_HERO_IMAGES.low;
};

export const getScoreHeroImagePosition = (riskLevel = 'LOW') => {
  if (riskLevel === 'HIGH') return 'center 18%';
  if (riskLevel === 'MEDIUM') return 'center 16%';
  return 'center 14%';
};

export const pulseMediumVerdict = {
  scale: [1, 1.025, 1],
  filter: [
    'drop-shadow(0 0 0 rgba(250,204,21,0))',
    'drop-shadow(0 0 14px rgba(250,204,21,0.55))',
    'drop-shadow(0 0 0 rgba(250,204,21,0))'
  ]
};

export const pulseHighVerdict = {
  scale: [1, 1.045, 1],
  filter: [
    'drop-shadow(0 0 0 rgba(239,68,68,0))',
    'drop-shadow(0 0 26px rgba(239,68,68,0.85))',
    'drop-shadow(0 0 0 rgba(239,68,68,0))'
  ]
};

export const getScoreTheme = (score = 0) => {
  if (score < 40) {
    return {
      tone: 'high',
      color: '#ef4444',
      soft: '#fecaca',
      accent: '#7f1d1d',
      gradientStart: '#fb7185',
      gradientEnd: '#dc2626',
      glow: 'rgba(239,68,68,0.8)',
      textShadow: '0 3px 0 #7f1d1d, 0 10px 30px rgba(239,68,68,0.45)',
    };
  }

  if (score < 70) {
    return {
      tone: 'medium',
      color: '#f97316',
      soft: '#fed7aa',
      accent: '#9a3412',
      gradientStart: '#fdba74',
      gradientEnd: '#f97316',
      glow: 'rgba(249,115,22,0.8)',
      textShadow: '0 3px 0 #9a3412, 0 10px 30px rgba(249,115,22,0.45)',
    };
  }

  return {
    tone: 'low',
    color: '#22c55e',
    soft: '#bbf7d0',
    accent: '#166534',
    gradientStart: '#4ade80',
    gradientEnd: '#16a34a',
    glow: 'rgba(34,197,94,0.8)',
    textShadow: '0 3px 0 #166534, 0 10px 30px rgba(34,197,94,0.4)',
  };
};

export const parseMaxCostValue = (costText = '') => {
  const matches = String(costText).match(/[\d,]+/g);
  if (!matches?.length) return 0;
  const raw = matches.length > 1 ? matches[matches.length - 1] : matches[0];
  return parseInt(raw.replace(/,/g, ''), 10) || 0;
};

export const calculateTotalPotentialLoss = (analysisData) => {
  if (!analysisData) return 0;

  const topRiskCost = parseMaxCostValue(analysisData.ai_analysis?.top_risk?.estimated_repair_cost);
  const otherRiskCost = (analysisData.ai_analysis?.other_risks || []).reduce((sum, risk) => sum + parseMaxCostValue(risk.estimated_repair_cost), 0);
  const engineCost = (analysisData.ai_analysis?.engine_analysis?.known_issues || []).reduce((sum, issue) => sum + parseMaxCostValue(issue.estimated_repair_cost), 0);
  const negotiationCeiling = analysisData.ai_analysis?.negotiation_power?.total_repair_range_high || 0;
  const lossEstimate = analysisData.worthify_score?.loss_estimate || 0;

  return Math.max(topRiskCost + otherRiskCost + engineCost + lossEstimate, negotiationCeiling + lossEstimate, lossEstimate);
};

export const normalizeIssueKey = (text = '') => {
  const fillers = new Set([
    'but','not','and','or','the','a','an','is','has','have','with',
    'excessive','excessively','slight','slightly','light','lightly',
    'minor','major','advisory','dangerous','fail','failed','passed',
    'very','too','no','non','of','in','on','at','to','from','by',
    'significantly','reduced','adversely','affecting','for','as',
    'close','limit','near','worn','wear','side','inner','outer',
    'left','right','front','rear','offside','nearside','centre','center','central',
    'repeated','repeating','recurring','recurrent','ongoing','persistent',
    'chronic','potential','possible','likely','detected','noted',
    'issue','issues','problem','problems','risk','risks',
    'multiple','several','some','various','generic',
  ]);
  const cleaned = text
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = cleaned
    .split(' ')
    .filter((w) => w && !fillers.has(w))
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));
  return words.slice(0, 2).join(' ') || cleaned;
};

// MOT defect type → severity
export const FACT_SEVERITY = {
  DANGEROUS: 'HIGH',
  MAJOR: 'MEDIUM',
  FAIL: 'MEDIUM',
  ADVISORY: 'MEDIUM',
  MINOR: 'LOW',
};

// Realistic GBP repair cost ranges per defect theme (low, high)
export const THEME_COSTS = {
  oil_leak:   [250, 900],
  brakes:     [180, 650],
  tyres:      [120, 480],
  steering:   [180, 550],
  suspension: [220, 700],
  emissions:  [250, 900],
  lights:     [15, 120],
  exhaust:    [180, 500],
  electrical: [120, 450],
  corrosion:  [300, 1500],
  wipers:     [15, 60],
};

// Realistic "if ignored" consequences per theme
export const THEME_CONSEQUENCES = {
  oil_leak: [
    'Low oil level can seize the engine — full engine replacement starts around £3,500.',
    'Dripping oil contaminates belts, sensors and catalytic converters.',
    'Will fail the next MOT if leak becomes excessive.',
  ],
  brakes: [
    'Worn discs shorten pad life — you pay twice if not replaced together.',
    'Braking distance increases — real safety risk in the wet.',
    'Advisory today is almost always a MOT fail at the next test.',
  ],
  tyres: [
    'Below legal 1.6mm tread = instant MOT fail and £100 fine per tyre, plus 3 penalty points.',
    'Grip and stopping distance drop sharply in wet or cold weather.',
    'Uneven wear often signals a deeper suspension or alignment issue.',
  ],
  steering: [
    'Play in joints worsens — full steering rack replacement runs £800-£1,500.',
    'Affects tyre wear and straight-line stability at motorway speeds.',
    'Will become a MOT fail if play increases.',
  ],
  suspension: [
    'Failed bushes and arms damage tyres, alignment and ride comfort.',
    'Worn shocks increase stopping distance by up to 20%.',
    'Usually fails the next MOT if left untreated.',
  ],
  emissions: [
    'Can trigger limp mode and stop the car running normally.',
    'DPF/EGR repairs escalate fast — £600 minor → £2,500+ full replacement.',
    'Will fail emissions test at MOT.',
  ],
  lights: [
    'Easy MOT fail — the tester has to fail it, no exceptions.',
    'Cheap fix now, so no reason to leave it for the seller to explain.',
  ],
  exhaust: [
    'Leaks get louder and corrode the catalytic converter (£500+ to replace).',
    'Will fail MOT for noise and emissions.',
  ],
  electrical: [
    'Battery/charging issues strand the car without warning.',
    'Can mean a failing alternator — £350-£700 to replace.',
  ],
  corrosion: [
    'Structural corrosion = immediate MOT fail, often not economical to repair.',
    'Spreads every winter — cost only goes up.',
  ],
  wipers: [
    'MOT fail if wipers or washers do not clear the screen properly.',
  ],
};

export const formatCostRange = (low, high) => `£${low.toLocaleString()} - £${high.toLocaleString()}`;

export const classifyDefectTheme = (text = '') => {
  const t = text.toLowerCase();
  if (/(oil|leak|seal|gasket|sump)/.test(t)) return { theme: 'oil_leak', labelKey: 'defectThemeOilLeak', labelFb: 'Engine oil leak / seal weeping', severity: 'MEDIUM' };
  if (/(brake\s*(disc|pad|caliper|hose|pipe)|brake fluid)/.test(t)) return { theme: 'brakes', labelKey: 'defectThemeBrakes', labelFb: 'Brake component wear', severity: 'HIGH' };
  if (/(tyre|tire|tread|sidewall)/.test(t)) return { theme: 'tyres', labelKey: 'defectThemeTyres', labelFb: 'Tyre wear / condition', severity: 'MEDIUM' };
  if (/(steering|track\s*rod|tie\s*rod|rack|ball\s*joint)/.test(t)) return { theme: 'steering', labelKey: 'defectThemeSteering', labelFb: 'Steering / suspension joint wear', severity: 'MEDIUM' };
  if (/(suspension|shock|strut|spring|bush(ing)?|arm)/.test(t)) return { theme: 'suspension', labelKey: 'defectThemeSuspension', labelFb: 'Suspension component wear', severity: 'MEDIUM' };
  if (/(emission|lambda|co|hc|nox|dpf|particulate)/.test(t)) return { theme: 'emissions', labelKey: 'defectThemeEmissions', labelFb: 'Emissions system issue', severity: 'MEDIUM' };
  if (/(headl|bulb|indicator|lamp|light|rear light)/.test(t)) return { theme: 'lights', labelKey: 'defectThemeLights', labelFb: 'Lighting fault', severity: 'LOW' };
  if (/(exhaust|silencer|catalyst)/.test(t)) return { theme: 'exhaust', labelKey: 'defectThemeExhaust', labelFb: 'Exhaust system wear', severity: 'MEDIUM' };
  if (/(battery|charging|alternator)/.test(t)) return { theme: 'electrical', labelKey: 'defectThemeElectrical', labelFb: 'Electrical / charging issue', severity: 'MEDIUM' };
  if (/(corro|rust|struct)/.test(t)) return { theme: 'corrosion', labelKey: 'defectThemeCorrosion', labelFb: 'Corrosion / structural wear', severity: 'HIGH' };
  if (/(wiper|washer)/.test(t)) return { theme: 'wipers', labelKey: 'defectThemeWipers', labelFb: 'Wipers / washers', severity: 'LOW' };
  if (/(child seat|seat ?belt|belt)/.test(t)) return { theme: 'seatbelt', labelKey: 'defectThemeSeatbelt', labelFb: 'Seatbelt / restraint check', severity: 'LOW' };
  if (/(screen|glass|chip|crack)/.test(t)) return { theme: 'screen', labelKey: 'defectThemeScreen', labelFb: 'Windscreen / glass', severity: 'LOW' };
  return { theme: normalizeIssueKey(text), labelKey: null, labelFb: text.length > 80 ? text.slice(0, 80) + '…' : text, severity: 'MEDIUM' };
};

export const buildDetectedIssues = (analysisData, t = null) => {
  if (!analysisData) return [];

  const tx = (key, fb) => (t && key ? (t(key) || fb) : fb);

  const tests = analysisData.tests || [];
  const recurring = analysisData.mot_stats?.recurring_issues || [];

  const themeMap = {};
  tests.forEach((test, idx) => {
    const defects = test.defects || [];
    const isLatest = idx === 0;
    defects.forEach((d) => {
      const raw = d.text || '';
      const text = d.translated_text || raw;
      if (!text || text.length < 6) return;
      // Classify on the ENGLISH original — theme regexes are English-keyed.
      const { theme, labelKey, labelFb } = classifyDefectTheme(raw || text);
      const finalSev = FACT_SEVERITY[(d.type || '').toUpperCase()] || 'MEDIUM';
      if (!themeMap[theme]) {
        themeMap[theme] = { title: tx(labelKey, labelFb), occurrences: 0, latest: false, severities: [], examples: [] };
      }
      themeMap[theme].occurrences += 1;
      if (isLatest) themeMap[theme].latest = true;
      themeMap[theme].severities.push(finalSev);
      if (themeMap[theme].examples.length < 2 && text.length < 160) {
        themeMap[theme].examples.push(text);
      }
    });
  });

  recurring.forEach((ri) => {
    const text = ri.issue || '';
    if (!text) return;
    const display = ri.issue_translated || text;
    const { theme, labelKey, labelFb } = classifyDefectTheme(text);
    if (!themeMap[theme]) {
      themeMap[theme] = { title: tx(labelKey, labelFb), occurrences: ri.count || 2, latest: false, severities: ['MEDIUM'], examples: [display] };
    }
    themeMap[theme].recurring = true;
    themeMap[theme].recurringCount = ri.count || themeMap[theme].occurrences;
  });

  const sevRank = { HIGH: 3, MEDIUM: 2, LOW: 1 };
  const worstSeverity = (arr) => arr.reduce((acc, s) => (sevRank[s] > sevRank[acc] ? s : acc), 'LOW');

  const items = Object.values(themeMap).map((th) => {
    const themeKey = Object.keys(THEME_COSTS).find(k => th.title.toLowerCase().includes(k.replace('_', ' ').slice(0, 4)))
      || (th.examples[0] ? Object.keys(THEME_COSTS).find(k => classifyDefectTheme(th.examples[0]).theme === k) : null);
    const costRange = THEME_COSTS[themeKey] || null;
    const baseDesc = th.examples[0] || th.title;
    const recurringSuffix = (th.recurring || th.occurrences >= 2)
      ? ` · ${tx('defectRecurringSuffix', 'Recurring in MOT — not fixed, may worsen over time.')}`
      : '';
    return {
      title: th.title,
      description: baseDesc + recurringSuffix,
      severity: worstSeverity(th.severities),
      occurrences: th.occurrences,
      latest: th.latest,
      recurring: !!(th.recurring || th.occurrences >= 2),
      themeKey: themeKey,
      examples: th.examples.slice(0, 3),
      cost: costRange ? formatCostRange(costRange[0], costRange[1]) : '',
      costMid: costRange ? Math.round((costRange[0] + costRange[1]) / 2) : 0,
      source: 'mot',
    };
  });

  items.sort((a, b) => {
    if (sevRank[b.severity] !== sevRank[a.severity]) return sevRank[b.severity] - sevRank[a.severity];
    if (a.latest !== b.latest) return a.latest ? -1 : 1;
    if (a.recurring !== b.recurring) return a.recurring ? -1 : 1;
    return b.occurrences - a.occurrences;
  });

  return items.slice(0, 6);
};

export const getVerdictNeonTheme = (riskLevel) => {
  switch (riskLevel) {
    case 'HIGH':
      return {
        bannerBackground: 'linear-gradient(180deg, rgba(220,38,38,0.96) 0%, rgba(185,28,28,0.98) 100%)',
        bannerFade: 'linear-gradient(90deg, rgba(220, 38, 38, 0.95) 0%, rgba(220, 38, 38, 0.88) 30%, rgba(220, 38, 38, 0.68) 50%, rgba(220, 38, 38, 0.34) 72%, rgba(220, 38, 38, 0) 100%)',
        borderColor: 'rgba(252,165,165,0.9)',
        boxShadow: '0 0 34px rgba(255,76,76,0.52), 0 0 110px rgba(239,68,68,0.28), 0 18px 36px rgba(127,29,29,0.30)',
        topGlow: 'transparent',
        titleShadow: '0 3px 0 rgba(127,29,29,0.76), 0 0 28px rgba(255,107,107,0.54), 0 0 64px rgba(255,59,59,0.26)',
        pillBorder: 'rgba(252,165,165,0.42)',
        pillBackground: 'rgba(127,29,29,0.22)',
        pillShadow: '0 0 16px rgba(248,113,113,0.22)',
        ctaBackground: 'linear-gradient(180deg, #86efac 0%, #22c55e 48%, #15803d 100%)',
        ctaBorder: 'rgba(220,252,231,0.95)',
        ctaShadow: '0 0 0 1px rgba(236,253,245,0.18) inset, 0 0 26px rgba(74,222,128,0.42), 0 0 70px rgba(34,197,94,0.18), 0 8px 0 rgba(21,128,61,0.98), 0 16px 24px rgba(21,128,61,0.28)',
        ctaTextShadow: '0 2px 0 rgba(20,83,45,0.72)',
        ctaIcon: 'text-emerald-50',
      };
    case 'LOW':
      return {
        bannerBackground: 'linear-gradient(180deg, rgba(34,197,94,0.94) 0%, rgba(21,128,61,0.98) 100%)',
        bannerFade: 'linear-gradient(90deg, rgba(34, 197, 94, 0.95) 0%, rgba(34, 197, 94, 0.88) 30%, rgba(34, 197, 94, 0.68) 50%, rgba(34, 197, 94, 0.34) 72%, rgba(34, 197, 94, 0) 100%)',
        borderColor: 'rgba(134,239,172,0.9)',
        boxShadow: '0 0 32px rgba(74,222,128,0.46), 0 0 96px rgba(34,197,94,0.22), 0 18px 34px rgba(20,83,45,0.24)',
        topGlow: 'transparent',
        titleShadow: '0 3px 0 rgba(21,128,61,0.72), 0 0 24px rgba(74,222,128,0.42), 0 0 56px rgba(34,197,94,0.18)',
        pillBorder: 'rgba(134,239,172,0.42)',
        pillBackground: 'rgba(20,83,45,0.18)',
        pillShadow: '0 0 16px rgba(74,222,128,0.18)',
        ctaBackground: 'linear-gradient(180deg, #86efac 0%, #22c55e 48%, #15803d 100%)',
        ctaBorder: 'rgba(220,252,231,0.95)',
        ctaShadow: '0 0 0 1px rgba(236,253,245,0.18) inset, 0 0 26px rgba(74,222,128,0.42), 0 0 70px rgba(34,197,94,0.18), 0 8px 0 rgba(21,128,61,0.98), 0 16px 24px rgba(21,128,61,0.28)',
        ctaTextShadow: '0 2px 0 rgba(20,83,45,0.72)',
        ctaIcon: 'text-emerald-50',
      };
    case 'MEDIUM':
    default:
      return {
        bannerBackground: 'linear-gradient(180deg, #fde047 0%, #facc15 58%, #eab308 100%)',
        bannerFade: 'linear-gradient(90deg, rgba(255, 193, 7, 0.95) 0%, rgba(255, 193, 7, 0.93) 18%, rgba(255, 193, 7, 0.82) 34%, rgba(255, 193, 7, 0.62) 52%, rgba(255, 193, 7, 0.34) 72%, rgba(255, 193, 7, 0.12) 88%, rgba(255, 193, 7, 0) 100%)',
        borderColor: 'rgba(250,204,21,0.98)',
        boxShadow: '0 0 36px rgba(250,204,21,0.62), 0 0 110px rgba(245,158,11,0.20), 0 18px 32px rgba(161,98,7,0.20)',
        topGlow: 'transparent',
        titleShadow: '0 2px 0 rgba(146,64,14,0.22)',
        pillBorder: 'rgba(146,64,14,0.24)',
        pillBackground: 'rgba(255,255,255,0.18)',
        pillShadow: 'none',
        ctaBackground: 'linear-gradient(180deg, #86efac 0%, #22c55e 48%, #15803d 100%)',
        ctaBorder: 'rgba(220,252,231,0.95)',
        ctaShadow: '0 0 0 1px rgba(236,253,245,0.18) inset, 0 0 26px rgba(74,222,128,0.42), 0 0 70px rgba(34,197,94,0.18), 0 8px 0 rgba(21,128,61,0.98), 0 16px 24px rgba(21,128,61,0.28)',
        ctaTextShadow: '0 2px 0 rgba(20,83,45,0.72)',
        ctaIcon: 'text-emerald-50',
      };
  }
};

export const LOADING_STEPS_KEYS = ['loadingStep1','loadingStep2','loadingStep3','loadingStep4','loadingStep5','loadingStep6'];

// 3 Worthify expert "cards" — intentionally ANONYMOUS (no AI brand names).
export const AI_PROVIDERS = [
  { key: 'openai',    role: 'aiRoleEngine' },
  { key: 'anthropic', role: 'aiRolePlatform' },
  { key: 'gemini',    role: 'aiRoleRisk' },
];
